// frontend/src/views/PrinterList.jsx

import React, { useState, useEffect } from 'react';
import { Plus, ArrowUpDown, QrCode, Settings, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import QRDisplay from '../components/QRDisplay';
import EditPrinterModal from '../components/EditPrinterModal';

function PrinterList({ onAddClick }) {
    const [printers, setPrinters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedId, setExpandedId] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    
    const [editingPrinter, setEditingPrinter] = useState(null);
    const [exporting, setExporting] = useState(false);

    const fetchPrinters = () => {
        setLoading(true);
        fetch('/api/printers')
            .then(async (res) => {
                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    throw new Error(errData.message || `Server Error: ${res.status}`);
                }
                return res.json();
            })
            .then(data => {
                if (Array.isArray(data)) {
                    setPrinters(data);
                } else {
                    throw new Error("Invalid data format.");
                }
                setLoading(false);
            })
            .catch(err => {
                console.error("Printer Fetch Error:", err);
                setError(err.message);
                setLoading(false);
            });
    };

    useEffect(() => { fetchPrinters(); }, []);

    const handleUpdate = async (id, data) => {
        try {
            const res = await fetch(`/api/printers/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (!res.ok) throw new Error("Update failed");
            setEditingPrinter(null);
            fetchPrinters();
        } catch (err) { alert(err.message); }
    };

    const handleDelete = async (id) => {
        try {
            const res = await fetch(`/api/printers/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error("Delete failed");
            setEditingPrinter(null);
            fetchPrinters();
        } catch (err) { alert(err.message); }
    };

    const handleExportFleet = async () => {
        setExporting(true);
        try {
            const res = await fetch('/api/printers/report/all', { method: 'POST' });
            const data = await res.json();
            if (res.ok && data.driveUrl) window.open(data.driveUrl, '_blank');
            else alert("Export failed: " + (data.message || 'Unknown'));
        } catch (err) { alert("Network Error"); } 
        finally { setExporting(false); }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedPrinters = [...printers].sort((a, b) => {
        const valA = a[sortConfig.key] || '';
        const valB = b[sortConfig.key] || '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    // --- STATUS LOGIC ---
    const getPrinterStatusList = (p) => {
        let statuses = [];
        const today = new Date();
        const curHours = p.current_print_hours || 0;

        const check = (lastDate, lastHours, daysLimit, hoursLimit, label) => {
            if (!lastDate) {
                statuses.push({ label, status: 'due', text: 'Never Done' });
                return;
            }
            const daysDiff = Math.ceil(Math.abs(today - new Date(lastDate)) / (86400000));
            if (daysDiff >= daysLimit) {
                const overdueBy = daysDiff - daysLimit;
                statuses.push({ label, status: 'due', text: `${overdueBy}d Overdue` });
                return;
            }
            if (hoursLimit > 0) {
                const hoursSince = curHours - (lastHours || 0);
                if (hoursSince >= hoursLimit) {
                    const overBy = (hoursSince - hoursLimit).toFixed(0);
                    statuses.push({ label, status: 'due', text: `${overBy}h Overdue` });
                    return;
                }
            }
            statuses.push({ label, status: 'ok', text: 'OK' });
        };

        // ?? NEW COLUMNS used here
        check(p.last_enclosure_date, 0, 14, 0, 'Enclosure');
        check(p.last_extruder_date, 0, 14, 0, 'Extruder');
        check(p.last_fans_date, 0, 14, 0, 'Fans');
        check(p.last_carbon_rods_date, p.last_carbon_rods_hours, 30, 200, 'Rods');
        check(p.last_lead_screws_date, p.last_lead_screws_hours, 90, 800, 'Screws');

        return statuses;
    };

    const StatusGrid = ({ items }) => {
        return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px' }}>
                {items.map((item, i) => {
                    const isDue = item.status === 'due';
                    const bgColor = isDue ? '#fef2f2' : '#ecfdf5';
                    const textColor = isDue ? '#b91c1c' : '#047857';
                    const borderColor = isDue ? '#fca5a5' : '#6ee7b7';
                    return (
                        <div key={i} style={{ 
                            backgroundColor: bgColor, color: textColor, border: `1px solid ${borderColor}`,
                            padding: '6px', borderRadius: '6px', fontSize: '0.75rem',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', lineHeight: '1.3'
                        }}>
                            <span style={{ fontWeight: '700', marginBottom: '2px' }}>{item.label}</span>
                            <span style={{ fontSize: '0.7rem' }}>{item.text}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (loading) return <div className="container text-center"><p>Loading Fleet...</p></div>;
    if (error) return <div className="container text-red"><p>Error: {error}</p></div>;

    return (
        <div className="container" style={{ maxWidth: '1200px' }}>
            {editingPrinter && <EditPrinterModal printer={editingPrinter} onClose={() => setEditingPrinter(null)} onUpdate={handleUpdate} onDelete={handleDelete} />}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>Printer Fleet Status</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="secondary" onClick={handleExportFleet} disabled={exporting} style={{ width: 'auto', display: 'flex', gap: '8px' }}>
                        <FileText size={18} /> {exporting ? 'Generating...' : 'Full Audit Report'}
                    </button>
                    <button onClick={onAddClick} style={{ width: 'auto', display: 'flex', gap: '8px' }}>
                        <Plus size={18} /> Add Printer
                    </button>
                </div>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th style={{width: '20%'}} onClick={() => handleSort('name')}>Name <ArrowUpDown size={14} style={{float:'right'}}/></th>
                            <th style={{width: '10%'}} onClick={() => handleSort('current_print_hours')}>Hrs <ArrowUpDown size={14} style={{float:'right'}}/></th>
                            <th style={{width: '55%'}}>Maintenance Health</th>
                            <th style={{width: '15%', textAlign: 'right'}}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedPrinters.length === 0 && (
                            <tr><td colSpan="4" className="text-center" style={{padding: '30px'}}>No printers found. Add one to get started!</td></tr>
                        )}
                        {sortedPrinters.map(p => {
                            // ?? SAFE CHECK
                            if (!p || !p.id) return null;
                            const statuses = getPrinterStatusList(p);
                            const hasIssues = statuses.some(s => s.status === 'due');
                            return (
                                <React.Fragment key={p.id}>
                                    <tr style={{ borderLeft: hasIssues ? '4px solid #ef4444' : '4px solid #10b981' }}>
                                        <td style={{ fontWeight: '600', fontSize: '1rem' }}>{p.name}<div style={{ fontSize:'0.75rem', color:'#94a3b8', fontWeight:'normal' }}>{p.model}</div></td>
                                        <td>{p.current_print_hours}</td>
                                        <td><StatusGrid items={statuses} /></td>
                                        <td style={{ textAlign: 'right' }}>
                                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                                                <button className="secondary" style={{ width: 'auto', padding: '6px 8px' }} onClick={() => setEditingPrinter(p)}><Settings size={16} /></button>
                                                <button className="secondary" style={{ width: 'auto', padding: '6px 8px' }} onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}><QrCode size={16} /> Label</button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedId === p.id && (
                                        <tr><td colSpan="4" style={{ backgroundColor: '#f8fafc', textAlign: 'center', padding: '20px' }}><QRDisplay qrKey={p.qr_code_key} printerName={p.name} /></td></tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default PrinterList;